import React, { useState, useEffect, useRef } from 'react';
import Lottie from 'lottie-react';

// Set to true to enable debug logs, false to disable
const DEBUG = false;

/**
 * ThinkingIndicator component that displays different messages based on confidence score
 * @param {number} confidenceScore - The confidence score received from SignalR (0-100)
 */
const ThinkingIndicator = ({ confidenceScore = 0 }) => {
  const [message, setMessage] = useState("Pokket is thinking...");
  const [animationData, setAnimationData] = useState(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const [cycleCompleted, setCycleCompleted] = useState(false);
  const lastMessageTimeRef = useRef(Date.now());
  const messageTimeoutRef = useRef(null);
  const prevConfidenceRef = useRef(confidenceScore);
  
  // Lottie animation options
  const lottieUrl = "https://db.pokketcoach.com/storage/v1/object/public/public-animations/lottie-animations/pokketthinking_loading.json";

  // Message lists
  const mediumConfidenceMessages = [
    "Pokket is thinking about what to ask...",
    "Pokket is considering where to take the conversation...",
    "Pokket is thinking about what to say..."
  ];
  
  const highConfidenceMessages = [
    "Pokket is figuring things out...",
    "Pokket is getting confident...",
    "Pokket is piecing things together..."
  ];
  
  const perfectConfidenceMessages = [
    "Pokket is ready to give advice!",
    "Pokket is considering a solution...",
    "Pokket is weighing different options...",
    "Pokket is coming up with a plan...",
    "Pokket is putting it all together..."
  ];

  // Helper function to get a random message from array
  const getRandomMessage = (messagesArray) => {
    const randomIndex = Math.floor(Math.random() * messagesArray.length);
    return messagesArray[randomIndex];
  };

  // Fetch the Lottie JSON data
  useEffect(() => {
    fetch(lottieUrl)
      .then(response => response.json())
      .then(data => {
        setAnimationData(data);
      })
      .catch(error => {
        console.error('Error loading Lottie animation:', error);
      });
  }, [lottieUrl]);

  // Reset state when confidence score becomes 0 (new user message)
  useEffect(() => {
    // If confidence drops to 0, we're in a new message cycle
    if (confidenceScore === 0 && prevConfidenceRef.current !== 0) {
      if (DEBUG) console.log('Confidence score reset to 0, resetting message state');
      setMessage("Pokket is thinking...");
      setMessageIndex(0);
      setCycleCompleted(false);
      lastMessageTimeRef.current = Date.now();
    }
    
    // Update previous confidence ref
    prevConfidenceRef.current = confidenceScore;
  }, [confidenceScore]);

  // Handle cycling messages for high confidence
  useEffect(() => {
    let cycleTimeout;
    
    // Only proceed with message cycling if confidence is 100+ and cycle isn't completed
    if (confidenceScore >= 100 && !cycleCompleted) {
      // Set initial message
      setMessage(perfectConfidenceMessages[messageIndex]);
      lastMessageTimeRef.current = Date.now();
      
      // Check if we've reached the last message in the cycle
      if (messageIndex < perfectConfidenceMessages.length - 1) {
        // Not the last message, set up timer for next message
        cycleTimeout = setTimeout(() => {
          setMessageIndex(messageIndex + 1);
        }, 4000);
      } else {
        // This is the last message, set up timer to revert to "thinking"
        cycleTimeout = setTimeout(() => {
          setCycleCompleted(true);
          setMessage("Pokket is thinking...");
          lastMessageTimeRef.current = Date.now();
        }, 4000);
      }
    }
    
    return () => {
      if (cycleTimeout) {
        clearTimeout(cycleTimeout);
      }
    };
  }, [confidenceScore, messageIndex, cycleCompleted]);

  // Handle confidence score changes with minimum display time
  useEffect(() => {
    // Skip this effect if confidence is 0 (initial state or reset) or 
    // we're in high confidence cycle mode and haven't completed the cycle
    if (confidenceScore === 0 || (confidenceScore >= 100 && !cycleCompleted)) {
      return;
    }
    
    // Clear any existing timeouts
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = null;
    }
    
    // Calculate time since the last message change
    const timeSinceLastMessage = Date.now() - lastMessageTimeRef.current;
    const minimumDisplayTime = 3000; // 3 seconds minimum display time
    
    // Log confidence score for debugging
    if (DEBUG) {
      console.log(`ThinkingIndicator: Current confidence score: ${confidenceScore}`);
    }
    
    // Function to update the message based on confidence
    const updateMessage = () => {
      let newMessage = "Pokket is thinking..."; // Default message
      
      if (confidenceScore >= 100) {
        if (DEBUG) console.log('ThinkingIndicator: Perfect confidence, cycle completed');
        // After cycle, show "thinking" message
      } else if (confidenceScore >= 70 && confidenceScore < 100) {
        if (DEBUG) console.log('ThinkingIndicator: High confidence');
        newMessage = getRandomMessage(highConfidenceMessages);
      } else if (confidenceScore >= 30 && confidenceScore < 70) {
        if (DEBUG) console.log('ThinkingIndicator: Medium confidence');
        newMessage = getRandomMessage(mediumConfidenceMessages);
      } else {
        if (DEBUG) console.log('ThinkingIndicator: Low confidence');
        // Use default "thinking" message
      }
      
      setMessage(newMessage);
      lastMessageTimeRef.current = Date.now();
    };
    
    // If we've displayed the current message for less than the minimum time
    if (timeSinceLastMessage < minimumDisplayTime) {
      // Schedule the update after the remaining time
      const remainingTime = minimumDisplayTime - timeSinceLastMessage;
      if (DEBUG) console.log(`Waiting ${remainingTime}ms before updating message`);
      
      messageTimeoutRef.current = setTimeout(() => {
        updateMessage();
      }, remainingTime);
    } else {
      // Update immediately if we've already displayed for the minimum time
      updateMessage();
    }
    
    // Cleanup timeout if component unmounts or confidence changes
    return () => {
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, [confidenceScore, cycleCompleted]);

  return (
    <div className="flex flex-col justify-center items-center w-full">
      {/* Lottie animation */}
      <div className="w-32 h-32 mb-3">
        {animationData && (
          <Lottie 
            animationData={animationData} 
            loop={true}
            autoplay={true}
          />
        )}
      </div>
      
      {/* Thinking message */}
      <div className="flex flex-col items-center text-center">
        <span className="text-gray-500 transition-opacity duration-300">
          {message}
        </span>
      </div>
    </div>
  );
};

export default ThinkingIndicator; 